import { useState } from 'react';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { CALL_CONTEXT, channelsToExecutive } from '../../lib/executiveRefusal';
import { describeMoneyRefusal, type MoneyRefusal } from '../../lib/moneyRefusal';
import redemptionsService, { type RedemptionRequest } from '../../services/redemptions.service';
import StepUpCodeField from '../security/StepUpCodeField';
import { colors, gradients } from '../../styles/theme';
import type { ProductBalance, UpcomingMaturity } from '../../types/finance';
import ExecutiveRefusal from './ExecutiveRefusal';
import RefusalNotice from './RefusalNotice';
import RequestCallButton from './RequestCallButton';
import { TERM_DATES_UNAVAILABLE, openTermKey, openTermLine } from './openTerms.helpers';
import {
  type Notice,
  NO_EARLY_EXIT_NOTICE,
  noticeLine,
  nonRenewalLabel,
  waitingNotices,
  withdrawableLine,
  beingRegistered,
  NOTICE_BEING_REGISTERED,
} from './noEarlyExit.helpers';

interface Props {
  readonly holding: ProductBalance;
  /** The terms funds says this holding is in today; null when funds could not say them. */
  readonly terms: UpcomingMaturity[] | null;
  /** The partner's redemption requests, to show a notice already given. */
  readonly requests: readonly RedemptionRequest[];
  readonly onDone: () => void;
}

/**
 * A series held to the end of its term (owner, 2026-11-01): there is no withdrawal form and no
 * early-withdrawal charge to accept. The partner reads that the term renews by itself, sees the
 * term's end as funds says it, and may give a notice of non-renewal (a scheduled redemption paid
 * at the end of the current term), or ask their Account Executive to call: never a dead end.
 */
export default function NoEarlyExitPanel({ holding, terms, requests, onDone }: Props): JSX.Element {
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);
  const [executiveRefusal, setExecutiveRefusal] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const notices = waitingNotices(requests, holding.product_id);
  // The notice covers the whole holding, never an amount (owner, 2026-11-01, S-61).
  const canSubmit = Number(holding.balance) > 0 && mfaCode.trim().length >= 6 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setRefusal(null);
    setExecutiveRefusal(null);
    try {
      await redemptionsService.request({
        product_id: holding.product_id,
        // The server takes the whole holding whatever amount it is sent; this is what it shows now.
        amount: holding.balance,
        currency_code: holding.currency_code,
        mfa_code: mfaCode.trim(),
      });
      setSent(true);
      setMfaCode('');
      onDone();
    } catch (e: unknown) {
      const designed = describeMoneyRefusal(e);
      if (designed !== null) setRefusal(designed);
      else if (channelsToExecutive(getApiErrorCode(e))) {
        setExecutiveRefusal(getApiErrorMessage(e, 'We could not record your request.'));
      } else setError(getApiErrorMessage(e, 'We could not record your request.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby="no-early-exit-title"
      className="rounded-lg p-4 space-y-3 text-sm"
      style={{ background: colors.warningBg, border: `1px solid ${colors.warning}`, color: colors.ink }}
    >
      <h3 id="no-early-exit-title" className="font-semibold m-0">This series has no early withdrawal</h3>
      <p className="m-0">{NO_EARLY_EXIT_NOTICE}</p>
      {terms === null && <p className="m-0">{TERM_DATES_UNAVAILABLE}</p>}
      {terms !== null && terms.length > 0 && (
        <ul className="list-disc pl-5 m-0">
          {terms.map((term) => (
            <li key={openTermKey(term)}>{openTermLine(term)}</li>
          ))}
        </ul>
      )}

      {notices.map((notice) => (
        <NoticeWithdrawal key={notice.id} notice={notice} onDone={onDone} />
      ))}
      {sent && notices.length === 0 && (
        <output className="block font-semibold">
          Your request not to renew was received. Your Account Executive will confirm it.
        </output>
      )}

      {refusal && <RefusalNotice refusal={refusal} />}
      {executiveRefusal && (
        <ExecutiveRefusal
          message={executiveRefusal}
          productId={holding.product_id}
          context={CALL_CONTEXT.withdrawal}
        />
      )}
      {error && (
        <p role="alert" className="m-0" style={{ color: colors.danger }}>{error}</p>
      )}

      {notices.length === 0 && !sent && (
        <div className="space-y-3">
          <StepUpCodeField
            id="nr-mfa"
            value={mfaCode}
            onChange={setMfaCode}
            hint="From your authenticator app."
          />
          <button
            type="button"
            onClick={() => { void submit(); }}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: canSubmit ? gradients.gold : colors.disabledBg,
              color: canSubmit ? colors.navy : colors.disabledText,
            }}
          >
            {busy ? 'Submitting…' : nonRenewalLabel(terms)}
          </button>
        </div>
      )}

      <p className="m-0">Questions about your term? Your Account Executive can help.</p>
      <RequestCallButton productId={holding.product_id} context={CALL_CONTEXT.withdrawal} />
    </section>
  );
}


/**
 * A notice already given, and the way to withdraw it until its last day to do so (owner,
 * 2026-11-01 and 2026-11-02, S-61): the term then renews by itself as if no notice had been given.
 * Two steps, so a tap is never a withdrawal. Until the fund answered the notice that day is not
 * known and the notice is shown as being registered, with no way to withdraw it yet; after that
 * day the server refuses it and the call to the Account Executive is offered with its sentence.
 */
function NoticeWithdrawal({
  notice,
  onDone,
}: Readonly<{ notice: Notice; onDone: () => void }>): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executiveRefusal, setExecutiveRefusal] = useState<string | null>(null);
  // The server says it, by the UTC day it reads (the viewer's calendar can be a day off).
  const beforeExitDay = notice.cancellable === true;

  const withdraw = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setExecutiveRefusal(null);
    try {
      await redemptionsService.cancel(notice.id, notice.version, '');
      onDone();
    } catch (e: unknown) {
      if (channelsToExecutive(getApiErrorCode(e))) {
        setExecutiveRefusal(getApiErrorMessage(e, 'We could not withdraw your notice.'));
      } else setError(getApiErrorMessage(e, 'We could not withdraw your notice.'));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="m-0 font-semibold">{noticeLine(notice)}</p>
      {beingRegistered(notice) && <p className="m-0">{NOTICE_BEING_REGISTERED}</p>}
      {withdrawableLine(notice) && <p className="m-0">{withdrawableLine(notice)}</p>}
      {notice.reinstated && (
        <p className="m-0">
          Your request to withdraw this notice could not be applied: the time to withdraw it had passed. Your money leaves this series as planned. Your Account Executive can explain it.
        </p>
      )}
      {beforeExitDay && !confirming && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={busy}
          className="underline text-sm"
          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer' }}
        >
          Withdraw my notice
        </button>
      )}
      {confirming && (
        <div className="space-y-2">
          <p className="m-0">If you withdraw your notice, your term renews by itself as if you had not given it.</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { void withdraw(); }}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{ background: colors.ink, color: colors.surface }}
            >
              {busy ? 'Withdrawing…' : 'Yes, withdraw it'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{ background: colors.surface, color: colors.ink, border: `1px solid ${colors.border}` }}
            >
              Keep my notice
            </button>
          </div>
        </div>
      )}
      {executiveRefusal && (
        <ExecutiveRefusal message={executiveRefusal} productId={notice.product_id} context={CALL_CONTEXT.withdrawal} />
      )}
      {error && <p role="alert" className="m-0" style={{ color: colors.danger }}>{error}</p>}
    </div>
  );
}
